import styled from 'styled-components'

export const RichTextContainer = styled.div`
    position: relative;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
    background: ${({ theme }) => theme.form.background};
    color: ${({ theme }) => theme.form.font};
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    overflow: clip;

    &:focus-within {
        border: ${({ theme }) => theme.form.textControl.activeBorder};
        box-shadow: ${({ theme }) => theme.form.textControl.activeBoxShadow};
        outline: 0;
    }

    &.is-required-error {
        border-color: #dc3545;
    }
`

export const ToolbarContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 4px 6px;
    border-bottom: 1px solid ${({ theme }) => theme.form.border};
    background: ${({ theme }) => theme.form.background};
    align-items: center;
`

export const ToolbarGroup = styled.div`
    display: flex;
    gap: 2px;
    align-items: center;

    &:not(:last-child)::after {
        content: '';
        display: block;
        width: 1px;
        height: 18px;
        background: ${({ theme }) => theme.form.border};
        margin: 0 2px;
    }
`

export const ToolbarButton = styled.button.attrs({ type: 'button' })`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 4px;
    background: ${({ $active, theme }) => $active ? theme.nav?.active || '#e0e0e0' : 'transparent'};
    color: ${({ theme }) => theme.form.font};
    cursor: pointer;
    font-size: 13px;
    padding: 0;
    transition: background 0.1s;

    &:hover {
        background: ${({ theme }) => theme.form.panelBg || '#f0f0f0'};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`

export const StyleSelect = styled.select`
    height: 28px;
    padding: 0 4px;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 4px;
    background: ${({ theme }) => theme.form.background};
    color: ${({ theme }) => theme.form.font};
    font-size: 12px;
    cursor: pointer;
    max-width: 110px;

    @media (max-width: 640px) {
        max-width: 80px;
        font-size: 11px;
    }

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.form.activeBorder};
    }
`

export const StyleDropdownWrapper = styled.div`
    position: relative;
`

export const StyleDropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 100;
    background: ${({ theme }) => theme.form.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 130px;
    padding: 4px 0;
    overflow: hidden;
`

export const StyleDropdownItem = styled.button.attrs({ type: 'button' })`
    display: block;
    width: 100%;
    text-align: left;
    padding: 6px 12px;
    border: none;
    background: ${({ $active, theme }) => $active ? theme.form.panelBg : 'transparent'};
    color: ${({ $active, theme }) => $active ? theme.form.font : theme.form.font};
    font-size: 13px;
    cursor: pointer;
    font-weight: ${({ $active }) => $active ? '600' : '400'};

    &:hover {
        background: ${({ theme }) => theme.form.panelBg};
    }
`

export const ColorPickerDropdown = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: ${({ $mobile }) => $mobile ? 'auto' : '0'};
    right: ${({ $mobile }) => $mobile ? '-100px' : 'auto'};
    z-index: 1000;
    background: ${({ theme }) => theme.form.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    padding: 10px;
    width: 230px;
`

export const ColorSwatchGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 6px;
    margin-bottom: 8px;
`

export const ColorSwatch = styled.button.attrs({ type: 'button' })`
    width: 20px;
    height: 20px;
    border-radius: 3px;
    border: 2px solid ${({ $active, theme }) => $active ? theme.form.font : 'transparent'};
    background: ${({ $color }) => $color};
    cursor: pointer;
    padding: 0;
    transition: transform 0.1s;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.15);

    &:hover {
        transform: scale(1.15);
    }
`

export const ColorPickerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 6px;
    border-top: 1px solid ${({ theme }) => theme.form.border};
`

export const ColorPickerInput = styled.input`
    width: 32px;
    height: 28px;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 4px;
    padding: 1px;
    cursor: pointer;
    background: none;
    flex-shrink: 0;
`

export const ColorPickerLabel = styled.span`
    font-size: 11px;
    color: ${({ theme }) => theme.form.font};
    opacity: 0.7;
`

export const ColorIconWrapper = styled.span`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    line-height: 1;
`

export const ColorBar = styled.span`
    display: block;
    width: 14px;
    height: 3px;
    border-radius: 1px;
    background: ${({ $color }) => $color || '#000'};
`

export const EditorWrapper = styled.div`
    position: relative;
    min-height: ${({ $rows }) => $rows ? `calc(1.5rem * ${$rows} + 0.75rem)` : '6rem'};
    ${({ $height }) => $height ? `height: ${$height}px; overflow-y: auto;` : ''}
`

export const ResizeHandle = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
    width: 16px;
    height: 16px;
    cursor: row-resize;
    user-select: none;

    /* Bottom-right corner triangle */
    &::after {
        content: '';
        position: absolute;
        bottom: 2px;
        right: 2px;
        width: 0;
        height: 0;
        border-bottom: 10px solid ${({ theme }) => theme.form.border};
        border-left: 10px solid transparent;
        opacity: 0.35;
        transition: opacity 0.15s;
    }

    &:hover::after {
        opacity: 0.8;
    }
`

export const EditorContent = styled.div`
    min-height: ${({ $rows }) => $rows ? `calc(1.5rem * ${$rows} + 0.75rem)` : '6rem'};
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.form.font};
    outline: none;
    caret-color: ${({ theme }) => theme.form.font};

    p {
        margin: 0 0 0.25rem 0;
    }

    h1 { font-size: 2em; font-weight: bold; margin: 0.5rem 0 0.25rem; }
    h2 { font-size: 1.5em; font-weight: bold; margin: 0.5rem 0 0.25rem; }
    h3 { font-size: 1.17em; font-weight: bold; margin: 0.5rem 0 0.25rem; }

    ul {
        padding-left: 1.5rem;
        margin: 0.25rem 0;
        list-style-type: disc;
    }

    ol {
        padding-left: 1.5rem;
        margin: 0.25rem 0;
        list-style-type: decimal;
    }

    li {
        display: list-item;
    }

    a {
        color: ${({ theme }) => theme.font.highlight};
        text-decoration: underline;
    }

    .editor-placeholder {
        position: absolute;
        top: 0.375rem;
        left: 0.75rem;
        color: ${({ theme }) => theme.form.placeholder || '#aaa'};
        pointer-events: none;
        user-select: none;
        font-size: 1rem;
    }
`
